import AddIcon from '@material-ui/icons/Add';
import LinkIcon from '@material-ui/icons/Link';
import MarkunreadIcon from '@material-ui/icons/Markunread';
import { IconButton, Tooltip } from '@mui/material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as ImageIcon } from '../../../../../../assets/icons/image.svg';
import { ReactComponent as ReplyIcon } from '../../../../../../assets/icons/reply.svg';
import { ReactComponent as SlackIcon } from '../../../../../../assets/icons/slack.svg';
import { ReactComponent as TrashIcon } from '../../../../../../assets/icons/trash.svg';
import { ReactComponent as TrelloIcon } from '../../../../../../assets/icons/trello.svg';
import { ReactComponent as VideoIcon } from '../../../../../../assets/icons/video.svg';
import useAppDispatch from '../../../../../../hooks/useAppDispatch';
import AttachmentPopup from '../../../../../../pages/NewWork/components/AttachmentPopup';
import { getMessageViewUrl } from '../../../../../../reducers/attachment';
import { userId } from '../../../../../../services/auth';
import { downloadFile, getWorkUrl } from '../../../../../../services/utils';
import { ConversationMessage, MessageAttachment, MessageReaction } from '../../../../../../types/chat';
import {
    DeletedMessageText,
    MessageActions,
    MessageAttachmentItem,
    MessageAttachmentsGrid,
    MessageBubble,
    MessageContainer,
    MessageContent,
    MessageContentWrapper,
    MessageMetaBox,
    MessageTextLink,
    MessageTimestamp,
    MessageWrapper,
    ReactionButton,
    ReactionChip,
    ReactionCount,
    ReactionEmoji,
    ReactionPickerPopover,
    ReactionPickerWrapper,
    ReactionsBox,
    ReplyAuthor,
    ReplyBox,
    ReplyContent,
    SenderBox,
    SenderName } from './styled';

type ConversationMessageProps = {
    message: ConversationMessage;
    showSender: boolean;
    isHovered: boolean;
    onHover: (messageId: number | null) => void;
    onReply: (message: ConversationMessage) => void;
    onDelete: (message: ConversationMessage) => void;
    onReact: (messageId: number, emoji: string) => void;
    onMarkUnread: (message: ConversationMessage) => void;
    allowReactions: boolean;
    reactions: MessageReaction[];
    currentUserAlias?: string;
};

const ConversationMessageComponent: React.FC<ConversationMessageProps> = ({
    message,
    showSender,
    isHovered,
    onHover,
    onReply,
    onDelete,
    onReact,
    onMarkUnread,
    allowReactions,
    reactions,
    currentUserAlias
}) => {
    const dispatch = useAppDispatch();
    const [ linkCopied, setLinkCopied ] = useState(false);
    const [ isReactionPickerOpen, setIsReactionPickerOpen ] = useState(false);
    const reactionPickerWrapperRef = useRef<HTMLDivElement>(null);
    const [ popupState, setPopupState ] = useState({
        isOpen: false,
        url: '',
        type: '',
        name: '',
        fileId: 0,
        currentIndex: 0
    });

    useEffect(() => {
        if (!isReactionPickerOpen) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (reactionPickerWrapperRef.current?.contains(target)) {
                return;
            }
            if (target.closest('.epr-emoji-category-content') !== null) {
                return;
            }
            setIsReactionPickerOpen(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [ isReactionPickerOpen ]);

    const handleReactionEmojiClick = (emojiData: EmojiClickData) => {
        onReact(message.id, emojiData.emoji);
        setIsReactionPickerOpen(false);
    };

    const handleAttachmentClick = async (attachment: MessageAttachment, index: number) => {
        try {
            const response = await dispatch(getMessageViewUrl({ attachmentId: attachment.id })).unwrap();
            setPopupState({
                isOpen: true,
                url: response.url,
                type: attachment.type,
                name: attachment.name,
                fileId: attachment.id,
                currentIndex: index
            });
        } catch (error) {
            console.error('Error fetching attachment URL:', error);
        }
    };

    const handleNextAttachment = async () => {
        const attachments = message.attachments;
        if (!attachments || attachments.length === 0) {
            return;
        }
        const nextIndex = (popupState.currentIndex + 1) % attachments.length;
        await handleAttachmentClick(attachments[nextIndex], nextIndex);
    };

    const handleDownloadAttachment = async () => {
        const response = await dispatch(
            getMessageViewUrl({ attachmentId: popupState.fileId })
        ).unwrap();
        await downloadFile(response.url, popupState.name || 'download');
    };

    const handleCopyLink = () => {
        const params = new URLSearchParams({
            conversationId: String(message.conversationId),
            entityId: message.entityId,
            messageId: String(message.id)
        });
        const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        navigator.clipboard.writeText(url).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        });
    };

    const renderSourceIcon = () => {
        switch (message.tagId) {
            case 'slack':
                return <SlackIcon width={12} height={12} />;
            case 'trello':
                return <TrelloIcon width={12} height={12} />;
            default:
                return null;
        }
    };

    const renderTextWithLink = (text: string) => {
        const combinedRegex = /(https?:\/\/[^\s]+)|(?:work item:\s*)(\w+)/gi;
        const result: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = combinedRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                result.push(text.slice(lastIndex, match.index));
            }

            if (match[1]) {
                const url = match[1].replace(/[.,!?)]+$/, '');
                const trailing = match[1].slice(url.length);
                result.push(
                    <MessageTextLink
                        key={match.index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {url}
                    </MessageTextLink>
                );
                if (trailing) {
                    result.push(trailing);
                }
            } else if (match[2] && Number.isInteger(Number(match[2]))) {
                const prefixLength = match[0].length - match[2].length;
                result.push(match[0].slice(0, prefixLength));
                result.push(
                    <MessageTextLink key={match.index} href={getWorkUrl(Number(match[2]))}>
                        {match[2]}
                    </MessageTextLink>
                );
            }

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            result.push(text.slice(lastIndex));
        }

        return <>{result}</>;
    };

    return (
        <>
            <MessageWrapper
                onMouseEnter={() => onHover(message.id)}
                onMouseLeave={() => onHover(null)}
            >
                {showSender && (
                    <SenderBox isOwn={message.isOwn}>
                        <SenderName variant="caption">
                            {message.isOwn
                                ? 'me'
                                : message.senderAlias || 'Unknown'}
                        </SenderName>
                    </SenderBox>
                )}

                <MessageContainer isOwn={message.isOwn}>
                    <MessageContentWrapper>
                        {(isHovered || isReactionPickerOpen) && (
                            <MessageActions isOwn={message.isOwn}>
                                <IconButton
                                    size="small"
                                    title="Reply to message"
                                    onClick={() => onReply(message)}
                                >
                                    <ReplyIcon width={16} height={16} />
                                </IconButton>
                                <Tooltip
                                    title={linkCopied ? 'Link copied!' : 'Copy link'}
                                    placement="top"
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyLink}
                                    >
                                        <LinkIcon style={{ width: 16, height: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Mark as unread" placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={() => onMarkUnread(message)}
                                    >
                                        <MarkunreadIcon style={{ width: 16, height: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                {message.isOwn && !message.isDeleted ? (
                                    <IconButton
                                        title="Delete message"
                                        size="small"
                                        onClick={() => onDelete(message)}
                                    >
                                        <TrashIcon width={16} height={16} />
                                    </IconButton>
                                ) : (
                                    allowReactions &&
                                    !message.isDeleted && (
                                        <ReactionPickerWrapper ref={reactionPickerWrapperRef}>
                                            <Tooltip title="Add reaction" placement="top">
                                                <ReactionButton
                                                    size="small"
                                                    onClick={() =>
                                                        setIsReactionPickerOpen((prev) => !prev)
                                                    }
                                                >
                                                    <AddIcon style={{ width: 16, height: 16 }} />
                                                </ReactionButton>
                                            </Tooltip>
                                            {isReactionPickerOpen && (
                                                <ReactionPickerPopover isOwn={message.isOwn}>
                                                    <EmojiPicker
                                                        onEmojiClick={handleReactionEmojiClick}
                                                        width={320}
                                                        height={380}
                                                    />
                                                </ReactionPickerPopover>
                                            )}
                                        </ReactionPickerWrapper>
                                    )
                                )}
                            </MessageActions>
                        )}
                        <MessageBubble isOwn={message.isOwn}>
                            {message.replyMessage && (
                                <ReplyBox>
                                    <ReplyAuthor>
                                        {message.replyMessage.senderUserId !==
                                            undefined &&
                                        message.replyMessage.senderUserId ===
                                            userId()
                                            ? 'me'
                                            : message.replyMessage.senderAlias ||
                                            'Unknown'}
                                    </ReplyAuthor>
                                    <ReplyContent>
                                        {renderTextWithLink(message.replyMessage.text || '')}
                                    </ReplyContent>
                                </ReplyBox>
                            )}

                            {message.isDeleted ? (
                                <DeletedMessageText>
                                    Message deleted
                                </DeletedMessageText>
                            ) : (
                                <MessageContent>{renderTextWithLink(message.text)}</MessageContent>
                            )}

                            {!message.isDeleted &&
                                message.attachments &&
                                message.attachments.length > 0 && (
                                <MessageAttachmentsGrid>
                                    {message.attachments.map((attachment, index) => (
                                        <MessageAttachmentItem
                                            key={attachment.id}
                                            onClick={() => handleAttachmentClick(attachment, index)}
                                        >
                                            {attachment.type.startsWith('image/') ? (
                                                <ImageIcon width={14} height={14} />
                                            ) : (
                                                <VideoIcon width={14} height={14} />
                                            )}
                                            {attachment.name}
                                        </MessageAttachmentItem>
                                    ))}
                                </MessageAttachmentsGrid>
                            )}

                            <MessageMetaBox isOwn={message.isOwn}>
                                {renderSourceIcon()}
                                <MessageTimestamp variant="caption">
                                    {message.displayTimestamp}
                                </MessageTimestamp>
                            </MessageMetaBox>
                        </MessageBubble>

                        {allowReactions &&
                            !message.isDeleted &&
                            reactions.length > 0 && (
                            <ReactionsBox isOwn={message.isOwn}>
                                {reactions.map((reaction) => {
                                    const isActive = currentUserAlias
                                        ? reaction.users.includes(
                                            currentUserAlias
                                        )
                                        : false;
                                    const tooltipContent = reaction.users.length > 0
                                        ? reaction.users.join(', ')
                                        : reaction.emoji;
                                    return (
                                        <Tooltip
                                            key={reaction.emoji}
                                            title={tooltipContent}
                                            placement="top"
                                            arrow
                                        >
                                            <ReactionChip
                                                onClick={() =>
                                                    onReact(
                                                        message.id,
                                                        reaction.emoji
                                                    )
                                                }
                                                isActive={isActive}
                                            >
                                                <ReactionEmoji>
                                                    {reaction.emoji}
                                                </ReactionEmoji>
                                                <ReactionCount>
                                                    {reaction.count}
                                                </ReactionCount>
                                            </ReactionChip>
                                        </Tooltip>
                                    );
                                })}
                            </ReactionsBox>
                        )}
                    </MessageContentWrapper>
                </MessageContainer>
            </MessageWrapper>

            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={() => setPopupState((prev) => ({ ...prev, isOpen: false }))}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                fileId={popupState.fileId}
                currentIndex={popupState.currentIndex + 1}
                totalItems={message.attachments?.length ?? 1}
                onNext={handleNextAttachment}
                onDownload={handleDownloadAttachment}
            />
        </>
    );
};

export default ConversationMessageComponent;
